import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import trackingService, {
  ExportAllowlistRow,
  ExportRegisterResult,
  ExportTerminalChoice,
} from '../../services/tracking.service';

/**
 * "Export Carriers" admin page (Tracing → Export Carriers).
 *
 * A SEPARATE page and table from Allowed Carriers, deliberately. The import
 * allowlist is read by six import-live modules (tracking, sweep, allowlist,
 * data_sync, trace_router, registration_health), so adding a direction to it
 * would put a schema change underneath production import tracing. Export gets
 * its own table and touches none of them.
 *
 * TWO CONTROLS, ONE CODE PATH.
 *
 *   Register a booking — type one you already know, it registers now, and you
 *   see within seconds whether CMS has it and how many PROs it carries.
 *   Terminals + cap  — what the bulk sweep will pick up on its own tick.
 *
 * Both end at the same `register_booking`, so exercising the button exercises
 * the sweep. That is why the button exists first: it is the only way to get
 * IMMEDIATE feedback, and it is how you push a known booking in during
 * testing rather than waiting for a tick.
 *
 * WHY THE CAP COUNTS BOOKINGS. One booking becomes N gwiTrace entries — one
 * per CMS PRO, averaging 2.8 and reaching 30 (measured CMS-live 2026-09-10:
 * 4,139 open POH export PROs across 1,462 bookings). A cap counted in entries
 * would mean something different for every booking, so the TRACKING column
 * shows both numbers and the cap is read against the bookings one.
 *
 * DISABLING STOPS INTAKE, NOT TRACKING. Turning a terminal off stops the
 * sweep picking up NEW bookings; anything already tracked keeps polling. An
 * operator closing the tap is not asking to abandon boxes mid-journey.
 */

const REGISTER_STYLES: Record<string, { cls: string; icon: 'ok' | 'warn' }> = {
  ok: {
    cls: 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300',
    icon: 'ok',
  },
  no_orders: {
    cls: 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300',
    icon: 'warn',
  },
  no_eligible: {
    cls: 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300',
    icon: 'warn',
  },
  disabled: {
    cls: 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300',
    icon: 'warn',
  },
  invalid: {
    cls: 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300',
    icon: 'warn',
  },
  failed: {
    cls: 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300',
    icon: 'warn',
  },
};

/**
 * Live-saving cap cell. Mirrors the Allowed Carriers page's max-imports cell
 * so the two feel the same: flip the switch to move between Unlimited and a
 * cap, type a number and tab out (or press Enter) to save it.
 */
function MaxBookingsCell({
  row,
  onSave,
}: {
  row: ExportAllowlistRow;
  onSave: (row: ExportAllowlistRow, value: number | null) => Promise<void>;
}) {
  const [draft, setDraft] = useState<string>(
    row.max_bookings === null ? '' : String(row.max_bookings),
  );
  const [saving, setSaving] = useState(false);

  // Follow the saved value after the parent reloads.
  useEffect(() => {
    setDraft(row.max_bookings === null ? '' : String(row.max_bookings));
  }, [row.max_bookings]);

  const isCapped = row.max_bookings !== null;

  const flipMode = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (!isCapped) {
        const parsed = parseInt(draft.trim(), 10);
        // 50 bookings is roughly 140 entries at the measured 2.8 PROs per
        // booking — a pilot-sized default rather than an arbitrary one.
        const value = Number.isFinite(parsed) && parsed >= 0 ? parsed : 50;
        await onSave(row, value);
      } else {
        await onSave(row, null);
      }
    } finally {
      setSaving(false);
    }
  };

  const saveDraft = async () => {
    if (saving) return;
    const trimmed = draft.trim();
    if (trimmed === '') return;
    const parsed = parseInt(trimmed, 10);
    if (Number.isNaN(parsed) || parsed < 0) {
      setDraft(row.max_bookings === null ? '' : String(row.max_bookings));
      return;
    }
    if (parsed === row.max_bookings) return;
    setSaving(true);
    try {
      await onSave(row, parsed);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        role="switch"
        aria-checked={isCapped}
        aria-label={isCapped ? 'Capped — click to make unlimited' : 'Unlimited — click to set a cap'}
        onClick={flipMode}
        disabled={saving}
        title={isCapped ? 'Click to make unlimited' : 'Click to cap bookings'}
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
          isCapped ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
        } disabled:opacity-50 disabled:cursor-wait`}
      >
        <span
          aria-hidden="true"
          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition-transform ${
            isCapped ? 'translate-x-[18px]' : 'translate-x-[2px]'
          }`}
        />
      </button>
      {isCapped ? (
        <input
          type="text"
          inputMode="numeric"
          value={draft}
          onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ''))}
          onBlur={saveDraft}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.currentTarget.blur();
            } else if (e.key === 'Escape') {
              setDraft(row.max_bookings === null ? '' : String(row.max_bookings));
              e.currentTarget.blur();
            }
          }}
          disabled={saving}
          className="w-16 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-body px-2 py-0.5 text-xs"
        />
      ) : (
        <span className="text-xs font-medium text-muted italic select-none">Unlimited</span>
      )}
      {saving && (
        <span className="text-xs text-gray-400 dark:text-gray-500 italic">saving…</span>
      )}
    </div>
  );
}

export default function ExportCarriers() {
  const [rows, setRows] = useState<ExportAllowlistRow[]>([]);
  const [terminals, setTerminals] = useState<ExportTerminalChoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add-terminal form
  const [newCode, setNewCode] = useState('');
  const [newCapMode, setNewCapMode] = useState<'limited' | 'unlimited'>('limited');
  const [newMaxBookings, setNewMaxBookings] = useState('50');
  const [adding, setAdding] = useState(false);

  // Register-a-booking form. Results accumulate so a testing session leaves a
  // readable trail rather than each attempt wiping the last.
  const [booking, setBooking] = useState('');
  const [registering, setRegistering] = useState(false);
  const [results, setResults] = useState<ExportRegisterResult[]>([]);

  // Per-row inline delete confirmation. Deliberately inline rather than a
  // modal: it is one row, the consequence is small (already-tracked entries
  // keep polling), and a dialog would be heavier than the decision.
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await trackingService.listExportAllowlist();
      setRows(data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load export carriers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    // The dropdown is a convenience, not a requirement — if gwiTrace is
    // unreachable the page still works and the code can be typed by hand.
    trackingService
      .listExportTerminals()
      .then(setTerminals)
      .catch(() => setTerminals([]));
  }, []);

  const addTerminal = async () => {
    const code = newCode.trim().toUpperCase();
    if (!code || adding) return;
    setAdding(true);
    setError(null);
    try {
      await trackingService.createExportAllowlist({
        carrier_code: code,
        enabled: true,
        max_bookings:
          newCapMode === 'unlimited' ? null : parseInt(newMaxBookings || '50', 10),
      });
      setNewCode('');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to add terminal');
    } finally {
      setAdding(false);
    }
  };

  const toggleEnabled = async (row: ExportAllowlistRow) => {
    setError(null);
    try {
      await trackingService.updateExportAllowlist(row.id, { enabled: !row.enabled });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to update terminal');
    }
  };

  const saveMaxBookings = async (row: ExportAllowlistRow, value: number | null) => {
    setError(null);
    try {
      await trackingService.updateExportAllowlist(row.id, { max_bookings: value });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to update cap');
    }
  };

  const removeTerminal = async (row: ExportAllowlistRow) => {
    setError(null);
    try {
      await trackingService.deleteExportAllowlist(row.id);
      setConfirmDelete(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to remove terminal');
    }
  };

  const registerBooking = async () => {
    const b = booking.trim();
    if (!b || registering) return;
    setRegistering(true);
    try {
      const res = await trackingService.registerExportBooking(b);
      setResults((prev) => [res, ...prev].slice(0, 12));
      if (res.status === 'ok') setBooking('');
      await load();
    } catch (e: any) {
      setResults((prev) =>
        [
          {
            booking: b,
            status: 'failed' as const,
            detail:
              e?.response?.data?.detail || e?.message || 'Request failed',
            registered: 0,
            pros: 0,
            errors: 0,
          },
          ...prev,
        ].slice(0, 12),
      );
    } finally {
      setRegistering(false);
    }
  };

  // Terminals already on the list shouldn't clutter the Add dropdown.
  const existing = new Set(rows.map((r) => r.carrier_code.toUpperCase()));
  const choices = terminals
    .filter((t) => t.code && !existing.has(t.code.toUpperCase()))
    .sort((a, b) => a.code.localeCompare(b.code));

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-body">Export Carriers</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 max-w-4xl">
          Turns on <strong>EXPORT</strong> tracing per terminal — both registering
          bookings and writing results back to CMS. Import is a separate switch on{' '}
          <strong>Import Carriers</strong>; enabling a terminal here does not turn on its
          imports, and disabling it here does not turn them off.
        </p>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 max-w-4xl">
          Each CMS PRO on a booking becomes one gwiTrace entry — including PROs with no
          container yet, which is the whole point: a CSR raises them before a driver picks
          an empty up.{' '}
          <strong>The cap counts bookings</strong>, not entries; one booking averages
          about 3 PROs.
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-800 dark:text-red-300">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Register a specific booking ------------------------------------ */}
      <div className="bg-panel rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h2 className="text-sm font-semibold text-body">Register a specific booking</h2>
        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400 max-w-3xl">
          Registers every open CMS PRO on one booking right now, without waiting for a
          sweep. Safe to press twice — gwiTrace matches on booking plus PRO, so nothing
          is duplicated.
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div className="min-w-[240px]">
            <label className="block text-xs font-medium text-label mb-1">Booking #</label>
            <input
              type="text"
              value={booking}
              onChange={(e) => setBooking(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') registerBooking();
              }}
              placeholder="NAM8677098"
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-body"
            />
          </div>
          <button
            type="button"
            onClick={registerBooking}
            disabled={registering || !booking.trim()}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowPathIcon className={`h-4 w-4 ${registering ? 'animate-spin' : ''}`} />
            {registering ? 'Registering…' : 'Register booking'}
          </button>
        </div>

        {results.length > 0 && (
          <ul className="mt-3 space-y-1">
            {results.map((r, i) => {
              const style = REGISTER_STYLES[r.status] || REGISTER_STYLES.failed;
              return (
                <li
                  key={`${r.booking}-${i}`}
                  className={`flex items-start gap-2 rounded px-3 py-1.5 text-xs ${style.cls}`}
                >
                  {style.icon === 'ok' ? (
                    <CheckCircleIcon className="h-4 w-4 shrink-0" />
                  ) : (
                    <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
                  )}
                  <span>
                    <strong>{r.booking}</strong> — {r.detail}
                    {r.errors > 0 && ` (${r.errors} error${r.errors === 1 ? '' : 's'})`}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Add a terminal -------------------------------------------------- */}
      <div className="bg-panel rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h2 className="text-sm font-semibold text-body">Add a terminal</h2>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div className="min-w-[240px]">
            <label className="block text-xs font-medium text-label mb-1">Terminal</label>
            <select
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-body"
            >
              <option value="">— select a terminal —</option>
              {choices.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.code}
                  {t.name ? ` — ${t.name}` : ''}
                  {t.is_active ? '' : ' (inactive in gwiTrace)'}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-label mb-1">Max bookings</label>
            <div className="flex items-center gap-2">
              <select
                value={newCapMode}
                onChange={(e) => setNewCapMode(e.target.value as 'limited' | 'unlimited')}
                className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-body"
              >
                <option value="limited">Cap at</option>
                <option value="unlimited">Unlimited</option>
              </select>
              {newCapMode === 'limited' && (
                <input
                  type="text"
                  inputMode="numeric"
                  value={newMaxBookings}
                  onChange={(e) => setNewMaxBookings(e.target.value.replace(/[^0-9]/g, ''))}
                  className="w-20 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-body"
                />
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={addTerminal}
            disabled={adding || !newCode.trim()}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PlusIcon className="h-4 w-4" />
            Add
          </button>
        </div>
      </div>

      {/* Terminals ------------------------------------------------------- */}
      <div className="bg-panel rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-semibold text-label uppercase">Code</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-label uppercase">Status</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-label uppercase">Max bookings</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-label uppercase">Tracking</th>
              <th className="px-4 py-2 text-right text-xs font-semibold text-label uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {loading && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-sm text-muted">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-sm text-muted">
                  No terminals yet. Add one above, or register a booking directly —
                  that works without a terminal on this list.
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={row.id}>
                <td className="px-4 py-2 text-sm text-body font-medium">{row.carrier_code}</td>
                <td className="px-4 py-2">
                  <button
                    type="button"
                    onClick={() => toggleEnabled(row)}
                    title={
                      row.enabled
                        ? 'Enabled — the sweep will pick up new bookings. Click to stop intake.'
                        : 'Disabled — no new bookings. Anything already tracked keeps polling.'
                    }
                    className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${
                      row.enabled
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-gray-100 text-label dark:bg-gray-800'
                    }`}
                  >
                    {row.enabled ? 'Enabled' : 'Disabled'}
                  </button>
                </td>
                <td className="px-4 py-2">
                  <MaxBookingsCell row={row} onSave={saveMaxBookings} />
                </td>
                <td className="px-4 py-2 text-xs text-gray-600 dark:text-gray-400">
                  {row.tracked_bookings === 0 ? (
                    <span className="text-muted">—</span>
                  ) : (
                    <>
                      {row.tracked_bookings} booking{row.tracked_bookings === 1 ? '' : 's'}
                      {' · '}
                      {row.tracked_entries} entr{row.tracked_entries === 1 ? 'y' : 'ies'}
                    </>
                  )}
                </td>
                <td className="px-4 py-2 text-right">
                  {confirmDelete === row.id ? (
                    <span className="inline-flex items-center gap-2 text-xs">
                      <span className="text-muted">Remove?</span>
                      <button
                        type="button"
                        onClick={() => removeTerminal(row)}
                        className="rounded bg-red-600 px-2 py-0.5 font-medium text-white hover:bg-red-700"
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(null)}
                        className="rounded border border-gray-300 dark:border-gray-600 px-2 py-0.5 text-body"
                      >
                        Cancel
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(row.id)}
                      title="Remove from the export allowlist (tracked entries keep polling)"
                      className="text-gray-400 hover:text-red-600"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
